import { useMemo, useState, type ComponentType } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
  icon?: ComponentType<{ className?: string }>;
}

export interface FilterOptionListProps {
  label: string;
  options: readonly FilterOption[];
  value: readonly string[];
  onChange: (values: string[]) => void;
  /** The options are still being fetched: an empty list is not an answer yet. */
  pending?: boolean;
  /** Above this many options the list grows a search field. */
  searchThreshold?: number;
  className?: string;
}

/**
 * Las opciones de una faceta: el buscador, la lista y el «Quitar selección».
 *
 * Vive aparte del desplegable que la abre porque tiene DOS casas. Suelta en la
 * barra es el contenido de `FilterSelect`, el popover de siempre; adentro del
 * panel de filtros de Empresas es una sección abierta en el sitio, sin segunda
 * capa —un popover encima de otro tapaba las otras siete facetas justo cuando
 * la gracia del panel es poder ponerlas todas de una vez—. Es el MISMO control
 * en los dos lados: se extrajo, no se volvió a escribir.
 */
export function FilterOptionList({
  label,
  options,
  value,
  onChange,
  pending = false,
  searchThreshold = 8,
  className,
}: FilterOptionListProps) {
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(value), [value]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, query]);

  const toggle = (optionValue: string) => {
    onChange(
      selected.has(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue],
    );
  };

  return (
    <div className={className}>
      {options.length > searchThreshold && (
        <div className="border-b border-border p-1.5">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Buscar ${label.toLowerCase()}…`}
            aria-label={`Buscar en ${label}`}
            className="h-7"
          />
        </div>
      )}

      <div
        role="listbox"
        aria-multiselectable
        aria-label={label}
        aria-busy={pending}
        className="max-h-64 overflow-y-auto scrollbar-thin p-1"
      >
        {pending && visible.length === 0 && (
          <div className="space-y-1.5 p-1.5">
            {[0, 1, 2, 3].map((row) => (
              <Skeleton key={row} className="h-4 w-full" />
            ))}
          </div>
        )}
        {!pending && visible.length === 0 && (
          <p className="px-2 py-6 text-center text-xs text-muted-foreground">Sin coincidencias</p>
        )}
        {visible.map((option) => {
          const isSelected = selected.has(option.value);
          const Icon = option.icon;
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => toggle(option.value)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors duration-150 ease-out hover:bg-muted"
            >
              <span
                className={cn(
                  'grid size-4 shrink-0 place-items-center rounded-sm border',
                  isSelected
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input',
                )}
              >
                {isSelected && <Check className="size-3" />}
              </span>
              {Icon && <Icon className="size-3.5 text-muted-foreground" />}
              <span className="truncate">{option.label}</span>
              {option.count !== undefined && (
                <span className="ml-auto text-xs tabular text-muted-foreground">
                  {option.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {value.length > 0 && (
        <div className="border-t border-border p-1">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center"
            onClick={() => onChange([])}
          >
            Quitar selección
          </Button>
        </div>
      )}
    </div>
  );
}

export interface FilterSelectProps extends FilterOptionListProps {
  /** Clases del botón que abre el desplegable. */
  className?: string;
}

/** Multi-select facet: the toolbar control every list view filters with. */
export function FilterSelect({
  label,
  options,
  value,
  onChange,
  pending = false,
  searchThreshold = 8,
  className,
}: FilterSelectProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1.5 border-dashed', value.length > 0 && 'border-solid', className)}
        >
          {label}
          {value.length > 0 && (
            <>
              <Separator orientation="vertical" className="mx-0.5 h-4" />
              <Badge variant="secondary" className="tabular">
                {value.length}
              </Badge>
            </>
          )}
          <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-56 p-0">
        <FilterOptionList
          label={label}
          options={options}
          value={value}
          onChange={onChange}
          pending={pending}
          searchThreshold={searchThreshold}
        />
      </PopoverContent>
    </Popover>
  );
}
